// perfBeacon.ts · v12.10.20 (T165 #1937: sendListPerf - every Messages action: first cards, full list, longest frozen time, false empty, rows, cards drawn) · 2026-10-24 · VoiceSpectrum AI
// perfBeacon.ts · v12.10.13 (#1554: a timing that overlaps a hidden-tab period is discarded) · v12.10.12 (#1540 T71 Performance Analyzer) · 2026-10-10 · VoiceSpectrum AI
// The page tells Support Ops how long its list took to appear on screen and how many rows it held - numbers only (no names,
// phone numbers or search text). Fire and forget: a failed beacon is ignored and never delays the screen.
import api from './api';

export type PerfPage = 'messages' | 'followups' | 'message';

// v12.10.13 (#1554): a background tab is throttled by the browser, so its wait is not the app's speed (Messages max 73.5 s
// on 10/10 was a hidden tab). Remember when the tab was last hidden; a measurement that started before the tab came back
// to the front - or that is taken while hidden - is dropped.
let hiddenUntil = typeof document !== 'undefined' && document.visibilityState === 'hidden' ? Number.POSITIVE_INFINITY : -1;
try {
  document.addEventListener('visibilitychange', () => {
    hiddenUntil = document.visibilityState === 'hidden' ? Number.POSITIVE_INFINITY : performance.now();
  });
} catch { /* no document (tests) */ }

/** True when the span [now - ms, now] overlapped a time the tab was hidden. Exported for tests. */
export function overlapsHidden(nowMs: number, ms: number, lastVisibleAt: number): boolean {
  return lastVisibleAt === Number.POSITIVE_INFINITY || (lastVisibleAt >= 0 && nowMs - ms <= lastVisibleAt);
}

export function sendPerf(page: PerfPage, ms: number, fetchMs: number, rows: number): void {
  if (!(ms > 0) || ms > 600_000) return;
  try { if (overlapsHidden(performance.now(), ms, hiddenUntil)) return; } catch { /* keep the beacon */ }
  try {
    void api.post('/t2/perf/page', { page, ms: Math.round(ms), fetchMs: Math.round(Math.max(0, fetchMs)), rows: Math.max(0, Math.round(rows)) }).catch(() => { /* ignored */ });
  } catch { /* ignored */ }
}

/** Run after the browser has painted the new list (two frames), then report. */
export function afterPaint(fn: () => void): void {
  try { requestAnimationFrame(() => requestAnimationFrame(fn)); } catch { setTimeout(fn, 0); }
}

// ══ v12.10.20 (T165 #1937): WHAT STAFF FEEL ON MESSAGES. ═══════════════════════════════════════════════════════════════
// The browser's own "long task" records (anything that kept the page from answering for 50 ms or more) are kept for a minute,
// so an action can report the longest stretch the screen was frozen while it ran. Numbers only - no caller data, no search text.
export type ListAction = 'open' | 'dates' | 'sort' | 'pill' | 'filter' | 'search';
const longTasks: { start: number; end: number }[] = [];
try {
  if (typeof PerformanceObserver !== 'undefined' && (PerformanceObserver.supportedEntryTypes ?? []).includes('longtask')) {
    new PerformanceObserver((list) => {
      for (const e of list.getEntries()) longTasks.push({ start: e.startTime, end: e.startTime + e.duration });
      const cut = performance.now() - 60_000;
      while (longTasks.length && longTasks[0].end < cut) longTasks.shift();
    }).observe({ type: 'longtask', buffered: true });
  }
} catch { /* no long-task support - frozen time reads 0 */ }

/** The longest long task that overlapped [from, to] (ms). Exported for tests. */
export function longestFrozen(from: number, to: number, tasks: { start: number; end: number }[] = longTasks): number {
  let max = 0;
  for (const t of tasks) if (t.end >= from && t.start <= to) max = Math.max(max, Math.min(t.end, to) - Math.max(t.start, from));
  return Math.round(max);
}

export interface ListPerf { action: ListAction; firstMs: number; fullMs: number; frozenMs: number; rows: number; drawn: number; falseEmpty: boolean }
export function sendListPerf(p: ListPerf): void {
  if (!(p.firstMs > 0) || p.firstMs > 600_000) return;
  try { if (overlapsHidden(performance.now(), Math.max(p.firstMs, p.fullMs), hiddenUntil)) return; } catch { /* keep it */ }
  try {
    void api.post('/t2/perf/list', {
      action: p.action, firstMs: Math.round(p.firstMs), fullMs: Math.round(Math.max(0, p.fullMs)), frozenMs: Math.round(Math.max(0, p.frozenMs)),
      rows: Math.max(0, Math.round(p.rows)), drawn: Math.max(0, Math.round(p.drawn)), falseEmpty: Boolean(p.falseEmpty),
    }).catch(() => { /* ignored */ });
  } catch { /* ignored */ }
}
